import { Button } from "@/components/ui/button";

function BannerCard({ banner, accent, testid, onResolveAnchor, dateNumber, dayLabel }) {
  if (!banner) return null;

  const isVip = accent === "vip";
  // Solid borders + solid overlay only (no bleeding gradients)
  const borderClass = isVip ? "border-amber-300" : "border-rose-500";
  const ribbonClass = isVip ? "bg-amber-300 text-emerald-950" : "bg-rose-500 text-white";

  const primaryCls = isVip
    ? "bg-amber-300 hover:bg-amber-400 text-emerald-950"
    : "bg-rose-500 hover:bg-rose-600 text-white";
  const secondaryCls = "border-2 border-white bg-transparent text-white hover:bg-white hover:text-emerald-950";

  const handleClick = (url) => (e) => {
    if (url?.startsWith("#")) {
      e.preventDefault();
      onResolveAnchor?.(url.slice(1));
    }
  };
  return (
    <article
      data-testid={testid}
      className={`relative isolate overflow-hidden rounded-2xl bg-emerald-950 border-2 ${borderClass}`}
    >
      {banner.image_url && (
        <img
          src={banner.image_url}
          alt=""
          aria-hidden
          className="absolute inset-0 -z-10 h-full w-full object-cover"
          loading="lazy"
        />
      )}
      {/* Single solid dark overlay for legibility */}
      <div className="absolute inset-0 -z-10 bg-emerald-950/80" aria-hidden />

      <div className="relative p-8 sm:p-10 lg:p-12 min-h-[440px] flex flex-col justify-end text-white">
        {/* Date ribbon top-right */}
        <div className={`absolute top-0 right-0 ${ribbonClass} px-5 py-3 rounded-bl-2xl text-center min-w-[100px]`}>
          <div className="text-[9px] uppercase tracking-[0.25em] font-[Outfit] font-bold">{dayLabel}</div>
          <div className="font-[Cormorant_Garamond] text-4xl leading-none mt-0.5">{dateNumber}</div>
          <div className="text-[9px] uppercase tracking-[0.25em] font-[Outfit] font-bold mt-0.5">JULY 2026</div>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full font-[Outfit] font-bold ${ribbonClass}`}>
            {banner.badge_text || (isVip ? "VIP Experience" : "Main Event")}
          </span>
        </div>

        <h3 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-white max-w-xl">
          {banner.title}
        </h3>

        {banner.date_text && (
          <div className="font-[Outfit] text-amber-200 text-sm uppercase tracking-[0.2em] mt-3 font-semibold">
            {banner.date_text}
          </div>
        )}

        {banner.description && (
          <p className="font-[Outfit] text-base leading-relaxed text-emerald-50 mt-4 max-w-2xl">
            {banner.description}
          </p>
        )}

        <div className="flex flex-wrap gap-3 mt-6">
          <Button
            size="lg"
            data-testid={`${testid}-buy-tickets`}
            onClick={(e) => { e.preventDefault(); onResolveAnchor?.("tickets"); }}
            className={`rounded-full px-6 h-12 font-[Outfit] font-semibold ${primaryCls}`}
          >
            Buy Tickets
          </Button>
          {banner.primary_btn_label && banner.primary_btn_url && (
            <Button
              asChild={!banner.primary_btn_url?.startsWith("#")}
              onClick={banner.primary_btn_url?.startsWith("#") ? handleClick(banner.primary_btn_url) : undefined}
              size="lg"
              variant="outline"
              data-testid={`${testid}-primary`}
              className={`rounded-full px-6 h-12 font-[Outfit] font-semibold ${secondaryCls}`}
            >
              {banner.primary_btn_url?.startsWith("#") ? (
                <span>{banner.primary_btn_label}</span>
              ) : (
                <a href={banner.primary_btn_url || "#"} target={banner.primary_btn_url?.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
                  {banner.primary_btn_label}
                </a>
              )}
            </Button>
          )}
          {banner.secondary_btn_label && banner.secondary_btn_url && (
            <Button
              asChild={!banner.secondary_btn_url?.startsWith("#")}
              onClick={banner.secondary_btn_url?.startsWith("#") ? handleClick(banner.secondary_btn_url) : undefined}
              size="lg"
              variant="outline"
              data-testid={`${testid}-secondary`}
              className={`rounded-full px-6 h-12 font-[Outfit] font-semibold ${secondaryCls}`}
            >
              {banner.secondary_btn_url?.startsWith("#") ? (
                <span>{banner.secondary_btn_label}</span>
              ) : (
                <a href={banner.secondary_btn_url || "#"} target={banner.secondary_btn_url?.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
                  {banner.secondary_btn_label}
                </a>
              )}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function EventBanners({ content, onScrollToForms, onScrollToTickets, sectionId }) {
  const vip = content?.vip_banner;
  const main = content?.main_banner;
  if (!vip && !main) return null;

  const resolveAnchor = (id) => {
    if (id === "tickets") onScrollToTickets?.();
    else if (id === "forms") onScrollToForms?.();
  };

  return (
    <section id={sectionId} data-testid="event-banners" className="bg-white py-20 sm:py-24 border-t border-b border-emerald-100">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-rose-600 font-[Outfit] font-bold">
            July 17 &amp; 18, 2026 · Charlotte, NC
          </div>
          <h2 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl lg:text-5xl text-emerald-950 mt-3 leading-tight">
            Two unforgettable nights of community, capital and celebration.
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <BannerCard banner={vip} accent="vip" testid="banner-vip" dateNumber="17" dayLabel="FRIDAY" onResolveAnchor={resolveAnchor} />
          <BannerCard banner={main} accent="main" testid="banner-main" dateNumber="18" dayLabel="SATURDAY" onResolveAnchor={resolveAnchor} />
        </div>
      </div>
    </section>
  );
}
